import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import EDateText from '@/components/everridge/EDateText';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useSupportEmailRequestDetailApi } from './hooks/useSupportEmailRequestDetailApi';
import { useSupportEmailRequestMutations } from './hooks/useSupportEmailRequestMutations';
import { useSupportEmailRequestAttachmentDownload } from './hooks/useSupportEmailRequestAttachmentDownload';
import SupportEmailAiAnalysisStatusBadge from './components/SupportEmailAiAnalysisStatusBadge';
import {
    SupportEmailRequestCategory,
    SupportEmailRequestStatus,
    SupportEmailAiAnalysisStatus,
} from './types/support-email-request.types';

const SupportEmailRequestDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();
    const isNew = id === 'new';

    const { request, isLoading } = useSupportEmailRequestDetailApi(isNew ? undefined : id);
    const { createMutation, updateMutation, deleteMutation, reanalyzeMutation } =
        useSupportEmailRequestMutations();
    const { downloadAttachment } = useSupportEmailRequestAttachmentDownload();

    const [status, setStatus] = useState<SupportEmailRequestStatus>(SupportEmailRequestStatus.New);
    const [category, setCategory] = useState<SupportEmailRequestCategory>(
        SupportEmailRequestCategory.Support,
    );
    const [sender, setSender] = useState('');
    const [recipient, setRecipient] = useState('');
    const [fromHeader, setFromHeader] = useState('');
    const [subject, setSubject] = useState('');
    const [bodyPlain, setBodyPlain] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [isSuspicious, setIsSuspicious] = useState(false);
    const [suspiciousReason, setSuspiciousReason] = useState('');
    const [proposedAnswer, setProposedAnswer] = useState('');

    useEffect(() => {
        if (!request) return;
        setStatus(request.status);
        setCategory(request.category);
        setSender(request.sender);
        setRecipient(request.recipient);
        setFromHeader(request.fromHeader ?? '');
        setSubject(request.subject ?? '');
        setBodyPlain(request.bodyPlain ?? '');
        setInternalNotes(request.internalNotes ?? '');
        setIsSuspicious(request.isSuspicious);
        setSuspiciousReason(request.suspiciousReason ?? '');
        setProposedAnswer(request.proposedAnswer ?? '');
    }, [request]);

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate('/superAdmin/support-requests');

    const handleSave = async () => {
        if (isNew) {
            await createMutation.mutateAsync({
                category,
                sender,
                recipient,
                fromHeader: fromHeader || null,
                subject: subject || null,
                bodyPlain: bodyPlain || null,
                internalNotes: internalNotes || null,
            });
            EToast({ variant: 'success', message: t('supportEmailRequests.toast.created') });
            goBack();
            return;
        }

        if (!id) return;

        await updateMutation.mutateAsync({
            id,
            body: {
                status,
                category,
                internalNotes: internalNotes || null,
                subject: subject || null,
                bodyPlain: bodyPlain || null,
                isSuspicious,
                suspiciousReason: suspiciousReason || null,
                proposedAnswer: proposedAnswer || null,
            },
        });
        EToast({ variant: 'success', message: t('supportEmailRequests.toast.updated') });
    };

    const handleDelete = async () => {
        if (!id || isNew) return;
        if (!window.confirm(t('supportEmailRequests.confirmDelete'))) return;
        await deleteMutation.mutateAsync(id);
        EToast({ variant: 'success', message: t('supportEmailRequests.toast.deleted') });
        goBack();
    };

    const hasAiResults = (req: NonNullable<typeof request>) =>
        req.aiAnalysisStatus === SupportEmailAiAnalysisStatus.Completed ||
        Boolean(req.aiSummary?.trim() || req.suspiciousReason?.trim() || req.proposedAnswer?.trim());

    const getSummaryPlaceholder = (req: NonNullable<typeof request>) => {
        if (req.aiAnalysisStatus === SupportEmailAiAnalysisStatus.Pending) {
            return t('supportEmailRequests.ai.analysisInProgress');
        }
        return t('supportEmailRequests.ai.summaryEmpty');
    };

    const handleReanalyze = async () => {
        if (!id || isNew || !request) return;

        if (hasAiResults(request) && !window.confirm(t('supportEmailRequests.ai.confirmReRun'))) {
            return;
        }

        await reanalyzeMutation.mutateAsync(id);
        EToast({ variant: 'success', message: t('supportEmailRequests.ai.analysisQueued') });
    };

    const isAnalysisPending = request?.aiAnalysisStatus === SupportEmailAiAnalysisStatus.Pending;
    const isSupportCategory = category === SupportEmailRequestCategory.Support;

    if (!isNew && isLoading) {
        return (
            <section className="flex flex-1 items-center justify-center p-6">
                <Loader2 className="size-8 animate-spin text-primary-color-1000" />
            </section>
        );
    }

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <section className="flex h-full min-h-0 flex-col overflow-auto p-6">
            <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                    <Button variant="ghost" onClick={goBack}>
                        {t('supportEmailRequests.backToList')}
                    </Button>
                    <h1 className="mt-2 text-2xl font-medium text-black">
                        {isNew ? t('supportEmailRequests.add') : t('supportEmailRequests.detailTitle')}
                    </h1>
                    {request && (
                        <p className="mt-1 text-sm text-gray">
                            {t('supportEmailRequests.receivedAt')}:{' '}
                            <EDateText date={new Date(request.receivedAt)} />
                        </p>
                    )}
                    {request && !isNew && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <SupportEmailAiAnalysisStatusBadge status={request.aiAnalysisStatus} />
                            {request.isSuspicious ? (
                                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                                    {t('supportEmailRequests.suspicious.flagged')}
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    {!isNew && (
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteMutation.isPending}
                        >
                            {t('elements.buttons.delete')}
                        </Button>
                    )}
                    <Button onClick={handleSave} disabled={isSaving}>
                        {t('elements.buttons.saveChanges')}
                    </Button>
                </div>
            </div>

            <div className="grid max-w-4xl grid-cols-1 gap-4">
                {isNew ? (
                    <>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.category')}</span>
                            <select
                                value={category}
                                onChange={(e) =>
                                    setCategory(Number(e.target.value) as SupportEmailRequestCategory)
                                }
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            >
                                <option value={SupportEmailRequestCategory.Support}>
                                    {t('supportEmailRequests.category.support')}
                                </option>
                                <option value={SupportEmailRequestCategory.Privacy}>
                                    {t('supportEmailRequests.category.privacy')}
                                </option>
                                <option value={SupportEmailRequestCategory.General}>
                                    {t('supportEmailRequests.category.general')}
                                </option>
                                <option value={SupportEmailRequestCategory.Unknown}>
                                    {t('supportEmailRequests.category.unknown')}
                                </option>
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.sender')}</span>
                            <input
                                value={sender}
                                onChange={(e) => setSender(e.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.recipient')}</span>
                            <input
                                value={recipient}
                                onChange={(e) => setRecipient(e.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.fromHeader')}</span>
                            <input
                                value={fromHeader}
                                onChange={(e) => setFromHeader(e.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.subject')}</span>
                            <input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.body')}</span>
                            <textarea
                                value={bodyPlain}
                                onChange={(e) => setBodyPlain(e.target.value)}
                                rows={8}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            />
                        </label>
                    </>
                ) : (
                    <>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.status')}</span>
                            <select
                                value={status}
                                onChange={(e) =>
                                    setStatus(Number(e.target.value) as SupportEmailRequestStatus)
                                }
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            >
                                <option value={SupportEmailRequestStatus.New}>
                                    {t('supportEmailRequests.status.new')}
                                </option>
                                <option value={SupportEmailRequestStatus.Ongoing}>
                                    {t('supportEmailRequests.status.ongoing')}
                                </option>
                                <option value={SupportEmailRequestStatus.Done}>
                                    {t('supportEmailRequests.status.done')}
                                </option>
                                <option value={SupportEmailRequestStatus.Cancelled}>
                                    {t('supportEmailRequests.status.cancelled')}
                                </option>
                                <option value={SupportEmailRequestStatus.NotRelevant}>
                                    {t('supportEmailRequests.status.notRelevant')}
                                </option>
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span className="text-gray">{t('supportEmailRequests.fields.category')}</span>
                            <select
                                value={category}
                                onChange={(e) =>
                                    setCategory(Number(e.target.value) as SupportEmailRequestCategory)
                                }
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                            >
                                <option value={SupportEmailRequestCategory.Support}>
                                    {t('supportEmailRequests.category.support')}
                                </option>
                                <option value={SupportEmailRequestCategory.Privacy}>
                                    {t('supportEmailRequests.category.privacy')}
                                </option>
                                <option value={SupportEmailRequestCategory.General}>
                                    {t('supportEmailRequests.category.general')}
                                </option>
                                <option value={SupportEmailRequestCategory.Unknown}>
                                    {t('supportEmailRequests.category.unknown')}
                                </option>
                            </select>
                        </label>
                        <div className="rounded-lg border border-lightgray bg-base p-4 text-sm">
                            <p>
                                <span className="text-gray">{t('supportEmailRequests.fields.sender')}: </span>
                                {request?.sender}
                            </p>
                            <p className="mt-1">
                                <span className="text-gray">
                                    {t('supportEmailRequests.fields.recipient')}:{' '}
                                </span>
                                {request?.recipient}
                            </p>
                            {request?.fromHeader && (
                                <p className="mt-1">
                                    <span className="text-gray">
                                        {t('supportEmailRequests.fields.fromHeader')}:{' '}
                                    </span>
                                    {request.fromHeader}
                                </p>
                            )}
                            {request?.subject && (
                                <p className="mt-1">
                                    <span className="text-gray">
                                        {t('supportEmailRequests.fields.subject')}:{' '}
                                    </span>
                                    {request.subject}
                                </p>
                            )}
                        </div>
                        {request?.bodyPlain && (
                            <label className="flex flex-col gap-1 text-sm">
                                <span className="text-gray">{t('supportEmailRequests.fields.body')}</span>
                                <pre className="whitespace-pre-wrap rounded-lg border border-lightgray bg-base p-4 text-sm text-black">
                                    {request.bodyPlain}
                                </pre>
                            </label>
                        )}
                        {request?.attachments && request.attachments.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <span className="text-sm text-gray">
                                    {t('supportEmailRequests.fields.attachments')}
                                </span>
                                {request.attachments.map((attachment) => (
                                    <div
                                        key={attachment.id}
                                        className="flex items-center justify-between rounded-lg border border-lightgray bg-base px-4 py-2"
                                    >
                                        <span className="text-sm text-black">{attachment.fileName}</span>
                                        <Button
                                            variant="outline"
                                            onClick={() => downloadAttachment(attachment.fileKey)}
                                        >
                                            {t('supportEmailRequests.downloadAttachment')}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {!isNew && request && (
                            <div className="space-y-4 rounded-lg border border-lightgray bg-base p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h2 className="text-base font-medium text-black">
                                        {t('supportEmailRequests.ai.sectionTitle')}
                                    </h2>
                                    {isSupportCategory ? (
                                        <Button
                                            variant="outline"
                                            onClick={handleReanalyze}
                                            disabled={reanalyzeMutation.isPending || isAnalysisPending}
                                        >
                                            {hasAiResults(request)
                                                ? t('supportEmailRequests.ai.reRunAnalysis')
                                                : t('supportEmailRequests.ai.runAnalysis')}
                                        </Button>
                                    ) : (
                                        <p className="text-sm text-gray">
                                            {t('supportEmailRequests.ai.supportCategoryOnly')}
                                        </p>
                                    )}
                                </div>
                                {(request.aiAnalysisStatus === SupportEmailAiAnalysisStatus.Failed ||
                                    request.aiAnalysisStatus === SupportEmailAiAnalysisStatus.Skipped) && (
                                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                                        {request.aiAnalysisError ??
                                            t('supportEmailRequests.ai.analysisFailedGeneric')}
                                    </div>
                                )}
                                <label className="flex flex-col gap-1 text-sm">
                                    <span className="text-gray">{t('supportEmailRequests.ai.summary')}</span>
                                    <textarea
                                        readOnly
                                        value={request.aiSummary ?? ''}
                                        placeholder={getSummaryPlaceholder(request)}
                                        rows={4}
                                        className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black read-only:cursor-default read-only:opacity-100"
                                    />
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={isSuspicious}
                                        onChange={(e) => setIsSuspicious(e.target.checked)}
                                        className="size-4"
                                    />
                                    <span>{t('supportEmailRequests.ai.markSuspicious')}</span>
                                </label>
                                <label className="flex flex-col gap-1 text-sm">
                                    <span className="text-gray">
                                        {t('supportEmailRequests.ai.suspiciousReason')}
                                    </span>
                                    <textarea
                                        value={suspiciousReason}
                                        onChange={(e) => setSuspiciousReason(e.target.value)}
                                        rows={2}
                                        className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-sm">
                                    <span className="text-gray">
                                        {t('supportEmailRequests.ai.proposedAnswer')}
                                    </span>
                                    <textarea
                                        value={proposedAnswer}
                                        onChange={(e) => setProposedAnswer(e.target.value)}
                                        rows={6}
                                        className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                                    />
                                </label>
                            </div>
                        )}
                    </>
                )}

                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-gray">{t('supportEmailRequests.fields.internalNotes')}</span>
                    <textarea
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        rows={4}
                        className="rounded-lg border border-lightgray bg-base px-3 py-2 text-black"
                    />
                </label>
            </div>
        </section>
    );
};

export default SupportEmailRequestDetailPage;
